import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, startWith, Subscription } from 'rxjs';
import { Table } from 'primeng/table';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';

@Injectable()
export abstract class BaseListComponent<T> implements OnInit, OnDestroy {
  abstract readonly store: any;
  protected sidebarId?: string;
  protected routerSubscription: Subscription = new Subscription();
  protected selectedId?: string | null;
  protected selectedTab?: string | null;
  protected selectedPanel?: string | null;
  protected menuItems?: MenuItem[];
  protected activeMenuItem?: MenuItem;
  protected stepsNavigateBack = 1;

  loading = false;
  searchWord = '';

  protected constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected messageService: MessageService,
    protected confirmationDialogService: ConfirmationDialogService,
    protected routingService: RoutingService
  ) {}

  /**
   * Determines whether the edit form overlay should be displayed.
   *
   * The method evaluates the current routing sidebar ID or the locally defined sidebar ID
   * to check if the overlay for the sidebar should be shown.
   *
   * @return {boolean} Returns true if the edit form overlay should be displayed; otherwise, returns false.
   */
  get editFormOverlay(): boolean {
    if (this.routingService.getSidebarIdFromRoute() || this.sidebarId) {
      return this.shouldShowOverlayForSidebar();
    }
    return false;
  }

  /**
   * Determines whether an overlay should be displayed for the sidebar
   * based on the current route information and the sidebar ID.
   *
   * @return {boolean} True if the overlay should be shown; otherwise, false.
   */
  private shouldShowOverlayForSidebar(): boolean {
    return (
      !!this.routingService.getIdFromRoute() &&
      !!this.sidebarId &&
      this.routingService.getSidebarIdFromRoute() === this.sidebarId
    );
  }

  /**
   * Retrieves the header text for the edit form overlay in the application.
   *
   * @return {string} The header text for the edit form overlay.
   */
  get editFormOverlayHeader(): string {
    return this.routingService.getEditFormOverlayHeader();
  }

  /**
   * Retrieves the overlay icon used in the edit form.
   *
   * This method accesses the `routingService` to fetch the appropriate
   * icon used for the edit form overlay within the application.
   *
   * @return {string} The icon identifier for the edit form overlay.
   */
  get editFormOverlayIcon(): string {
    return this.routingService.getEditFormOverlayIcon();
  }

  /**
   * Retrieves the overlay sub used in the edit form.
   *
   * This method accesses the `routingService` to fetch the appropriate
   * subline used for the edit form overlay within the application.
   *
   * @return {string} The subline for the edit form overlay.
   */
  get editFormOverlaySub(): string {
    return this.routingService.getEditFormOverlaySub();
  }

  /**
   * Width class of the edit form overlay (route-data `overlayWidth`,
   * default `w-8`).
   */
  get editFormOverlayWidth(): string {
    return this.routingService.getEditFormOverlayWidth();
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit(): void {
    this.setupRouteSubscription();
  }

  /**
   * Retrieves a parameter value from the route's parameter map.
   *
   * @param {string} param - The name of the parameter to retrieve from the route.
   * @return {string | null} The value of the specified parameter, or null if the parameter does not exist.
   */
  getParamFromRoute(param: string): string | null {
    return this.routingService
      .getDeepestChild(this.route)
      ?.snapshot?.paramMap.get(param);
  }

  /**
   * Updates the active tab state based on the current route parameters.
   *
   * This method fetches the tab parameter from the route, assigns it to the
   * selectedTab property, and updates the active item accordingly if the
   * selectedTab is defined.
   *
   * @return {void} Does not return a value.
   */
  setActiveTab(): void {
    this.selectedTab = this.getParamFromRoute('tab');
    if (this.selectedTab) this.setActiveItemByUrl();
  }

  /**
   * Sets the active panel for the application by determining the panel from the route parameters.
   * If a panel is identified, the corresponding item is set to active based on the URL.
   *
   * @return {void} Does not return a value.
   */
  setActivePanel(): void {
    this.selectedPanel = this.getParamFromRoute('panel');
    if (this.selectedPanel) this.setActiveItemByUrl();
  }

  /**
   * Sets the active menu item based on the current URL.
   * The method searches through the list of menu items to find a match
   * by checking if the routerLink of an item includes the currently selected tab.
   * Updates the activeMenuItem property with the matching menu item, if found.
   *
   * @return {void} This method does not return a value.
   */
  setActiveItemByUrl(): void {
    if (!this.menuItems) return;
    this.activeMenuItem = this.menuItems.find(item =>
      item.routerLink?.[0].includes(this.selectedTab)
    );
  }

  /**
   * Sets up a subscription to monitor route navigation events and updates
   * the active tab and panel accordingly. Ensures that there is only one active
   * subscription at a time to avoid memory leaks or redundant operations.
   *
   * The method listens for `NavigationEnd` events as well as handles the
   * scenario for the initial load by starting with the current router URL.
   *
   * @return {void} This method does not return a value.
   */
  private setupRouteSubscription(): void {
    // Ensure there's only one subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(this.router.url) // Ensure initial load is handled
      )
      .subscribe(() => {
        this.setActiveTab();
        this.setActivePanel();
      });
  }

  /**
   * Triggers a confirmation dialog for deletion.
   *
   * @param {Event | undefined} event - The event that initiated the confirmation, can be undefined.
   * @param {T} item - The item to be deleted.
   * @param {string} itemName - The name of the item to show in the confirmation message.
   * @param {string} [overrideMessage] - Optional custom message to override the default confirmation message.
   * @return {void} This method does not return a value.
   */
  confirm(
    event: Event | undefined,
    item: T,
    itemName: string,
    overrideMessage?: string
  ): void {
    this.confirmationDialogService.confirmDeletion(
      event,
      itemName,
      this.store.entityLabel(),
      () => this.remove(item),
      overrideMessage
    );
  }

  /**
   * Removes the specified item from the store and clears the current selection.
   *
   * @template T
   * @param {T} item - The item to be removed from the store.
   */
  protected remove = (item: T) => {
    this.store.deleteItem(item);
    this.store.deselect();
  };

  /**
   * Clears the provided table and resets the searchWord to an empty string.
   *
   * @param {Table} table - The table instance to be cleared.
   * @return {void} This method does not return any value.
   */
  // NG21-MIGRATION(stufe-3): Basis für 22 Listen – Table.clear()/filterGlobal hier zentral testen; Row-Actions-Umbau setzt hier an (§9.1, §13.3)
  clear(table: Table): void {
    table.clear();
    this.searchWord = '';
  }

  /**
   * Handles the overlay close action and navigates back to the previous step.
   *
   * @return {void} This method does not return any value.
   */
  onOverlayClose(): void {
    this.routingService.navigateBack(this.stepsNavigateBack);
  }

  /**
   * destroy component and unsubscribe router-subscriptions
   */
  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }
}
